// Copyright (c) 2026 Otyka LLC. All rights reserved.

import React, { useState } from 'react';
import './SentimentDonut.css';

const ORDER = ['POSITIVE', 'NEGATIVE', 'MIXED', 'NEUTRAL'];

export default function SentimentDonut({ counts, meta }) {
  const [hovered, setHovered] = useState(null);

  const total = ORDER.reduce((s, k) => s + (counts[k] || 0), 0);
  if (total === 0) return null;

  // Build SVG donut arcs
  const cx = 80, cy = 80, r = 60, strokeWidth = 22;
  const circumference = 2 * Math.PI * r;

  let cumulativeAngle = -90; // start at top

  const segments = ORDER.map((key) => {
    const value = counts[key] || 0;
    const pct = value / total;
    const angle = pct * 360;
    const startAngle = cumulativeAngle;
    cumulativeAngle += angle;

    // Convert to SVG stroke-dashoffset approach
    const dashLength = pct * circumference;
    const offsetStart = ((startAngle + 90) / 360) * circumference;

    return {
      key,
      value,
      pct,
      dashLength,
      offsetStart: circumference - offsetStart,
      color: meta[key].color,
    };
  }).filter((s) => s.value > 0);

  const activeSegment = hovered ? segments.find((s) => s.key === hovered) : null;
  const centerLabel = activeSegment
    ? { value: activeSegment.value, label: meta[activeSegment.key].label }
    : { value: total, label: 'Total' };

  return (
    <div className="sentiment-donut">
      <div className="sentiment-donut__chart-wrap">
        <svg
          viewBox="0 0 160 160"
          width="160"
          height="160"
          aria-label="Sentiment distribution donut chart"
          role="img"
        >
          {/* Background ring */}
          <circle
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke="var(--gray-100)"
            strokeWidth={strokeWidth}
          />
          {segments.map((seg) => (
            <circle
              key={seg.key}
              cx={cx} cy={cy} r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={hovered === seg.key ? strokeWidth + 4 : strokeWidth}
              strokeDasharray={`${seg.dashLength} ${circumference - seg.dashLength}`}
              strokeDashoffset={seg.offsetStart}
              strokeLinecap="butt"
              style={{ transition: 'stroke-width 0.15s', cursor: 'pointer' }}
              onMouseEnter={() => setHovered(seg.key)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(seg.key)}
              onBlur={() => setHovered(null)}
              tabIndex={0}
              aria-label={`${meta[seg.key].label}: ${seg.value} (${Math.round(seg.pct * 100)}%)`}
              role="graphics-symbol"
            />
          ))}
          {/* Center text */}
          <text x={cx} y={cy - 6} textAnchor="middle" className="donut-center-value">
            {centerLabel.value}
          </text>
          <text x={cx} y={cy + 14} textAnchor="middle" className="donut-center-label">
            {centerLabel.label}
          </text>
        </svg>
      </div>

      <ul className="sentiment-donut__legend" role="list">
        {ORDER.filter((k) => (counts[k] || 0) > 0).map((key) => (
          <li
            key={key}
            className={`sentiment-donut__legend-item${hovered === key ? ' sentiment-donut__legend-item--active' : ''}`}
            onMouseEnter={() => setHovered(key)}
            onMouseLeave={() => setHovered(null)}
          >
            <span
              className="sentiment-donut__legend-dot"
              style={{ background: meta[key].color }}
              aria-hidden="true"
            />
            <span className="sentiment-donut__legend-name">{meta[key].label}</span>
            <span className="sentiment-donut__legend-count">{counts[key]}</span>
            <span className="sentiment-donut__legend-pct">
              {Math.round(((counts[key] || 0) / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
